import React, { useState, useEffect, useMemo } from 'react';
import { sharingService, userService } from '../services/api';
import { useAuth } from '../context/AuthContext';

const ShareModal = ({ entityType, entityId, entityName, onClose, onSaved }) => {
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [sharedWithAll, setSharedWithAll] = useState(true);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersRes, sharingRes] = await Promise.all([
          userService.getShareableUsers(),
          entityType === 'folder'
            ? sharingService.getFolderSharing(entityId)
            : sharingService.getProjectSharing(entityId)
        ]);
        setAllUsers(usersRes.data || usersRes);
        const sharingData = sharingRes.data || sharingRes;
        setSharedWithAll(sharingData.sharedWithAll);
        if (!sharingData.sharedWithAll) {
          setSelectedUserIds(new Set(sharingData.users.map(u => u.id)));
        }
      } catch (err) {
        setError('Failed to load sharing data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [entityType, entityId]);

  // Exclude current user — they are always included automatically
  const otherUsers = useMemo(() => allUsers.filter(u => u.id !== currentUser?.id), [allUsers, currentUser]);

  const filteredUsers = useMemo(() => {
    let list = otherUsers;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(u =>
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
      );
    }
    // Sort: selected users first, then unselected
    return [...list].sort((a, b) => {
      const aSel = selectedUserIds.has(a.id) ? 1 : 0;
      const bSel = selectedUserIds.has(b.id) ? 1 : 0;
      return bSel - aSel;
    });
  }, [otherUsers, searchQuery, selectedUserIds]);

  const handleToggleUser = (userId) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    const all = new Set(otherUsers.map(u => u.id));
    if (currentUser?.id) all.add(currentUser.id);
    setSelectedUserIds(all);
  };

  const handleDeselectAll = () => {
    setSelectedUserIds(new Set(currentUser?.id ? [currentUser.id] : []));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const userIds = sharedWithAll ? [] : Array.from(selectedUserIds);

      if (!sharedWithAll && userIds.length === 0) {
        setError('Please select at least one user, or share with everyone');
        setSaving(false);
        return;
      }

      if (entityType === 'folder') {
        await sharingService.updateFolderSharing(entityId, userIds);
      } else {
        await sharingService.updateProjectSharing(entityId, userIds);
      }
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      setError('Failed to update sharing');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
      <div className="p-6 w-[440px] max-h-[80vh] flex flex-col" style={{ backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)' }}>
        <h3 className="text-sm font-bold text-white uppercase tracking-widest mb-1">
          Share {entityType === 'folder' ? 'Folder' : 'Project'}
        </h3>
        <p className="text-xs mb-4 truncate" style={{ color: 'rgba(255,255,255,0.5)' }}>
          {entityName}
          {entityType === 'folder' && (
            <span className="ml-1" style={{ color: 'rgba(255,255,255,0.35)' }}>
              (applies to all contents)
            </span>
          )}
        </p>

        {loading ? (
          <div className="py-8 text-center text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>Loading...</div>
        ) : (
          <>
            {/* Share with everyone toggle */}
            <label className="flex items-center gap-3 mb-4 cursor-pointer select-none">
              <div
                className="relative w-9 h-5 rounded-full transition-colors"
                style={{ backgroundColor: sharedWithAll ? 'var(--tqa-green)' : 'var(--tqa-surface-3)' }}
                onClick={() => setSharedWithAll(!sharedWithAll)}
              >
                <div
                  className="absolute top-0.5 w-4 h-4 rounded-full transition-transform"
                  style={{
                    backgroundColor: 'white',
                    transform: sharedWithAll ? 'translateX(18px)' : 'translateX(2px)',
                  }}
                />
              </div>
              <span className="text-xs uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.7)' }}>
                Share with everyone
              </span>
            </label>

            {/* User selection (when not sharing with everyone) */}
            {!sharedWithAll && (
              <div className="flex-1 min-h-0 flex flex-col">
                {/* Search */}
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 text-sm text-white focus:outline-none mb-2"
                  style={{ backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)' }}
                  placeholder="Search users..."
                  autoFocus
                />

                {/* Select all / deselect all */}
                <div className="flex gap-3 mb-2">
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="text-xs uppercase tracking-wider transition-colors hover:brightness-125"
                    style={{ color: 'var(--tqa-green)' }}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={handleDeselectAll}
                    className="text-xs uppercase tracking-wider transition-colors hover:brightness-125"
                    style={{ color: 'rgba(255,255,255,0.5)' }}
                  >
                    Deselect all
                  </button>
                </div>

                {/* User list */}
                <div className="overflow-y-auto max-h-[300px] -mx-1 px-1" style={{ scrollbarWidth: 'thin' }}>
                  {filteredUsers.map(user => (
                    <label
                      key={user.id}
                      className="flex items-center gap-3 px-2 py-2 cursor-pointer rounded transition-colors hover:brightness-110"
                      style={{ backgroundColor: selectedUserIds.has(user.id) ? 'rgba(130,255,207,0.06)' : 'transparent' }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedUserIds.has(user.id)}
                        onChange={() => handleToggleUser(user.id)}
                        className="accent-[var(--tqa-green)] w-4 h-4 flex-shrink-0"
                      />
                      {user.avatarUrl ? (
                        <img src={user.avatarUrl} alt="" className="w-6 h-6 rounded-full flex-shrink-0" />
                      ) : (
                        <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold"
                          style={{ backgroundColor: 'var(--tqa-surface-3)', color: 'rgba(255,255,255,0.5)' }}>
                          {(user.name || user.email || '?')[0].toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-white truncate">{user.name || 'Unnamed'}</div>
                        <div className="text-xs truncate" style={{ color: 'rgba(255,255,255,0.4)' }}>{user.email}</div>
                      </div>
                    </label>
                  ))}
                  {filteredUsers.length === 0 && (
                    <div className="py-4 text-center text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                      No users found
                    </div>
                  )}
                </div>

                {/* Selection count */}
                <div className="mt-2 text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  {selectedUserIds.size - (currentUser?.id && selectedUserIds.has(currentUser.id) ? 1 : 0)} of {otherUsers.length} users selected
                </div>
              </div>
            )}

            {error && <p className="text-xs mt-2" style={{ color: 'var(--tqa-red)' }}>{error}</p>}

            {/* Actions */}
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors"
                style={{ border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.5)', backgroundColor: 'transparent' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors hover:brightness-110"
                style={{ backgroundColor: 'var(--tqa-green)', color: '#1a1a1a' }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ShareModal;
